// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Reports
// Formatted reports with multi-measurement support for FEMA reimbursement
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, Stack, TextInput,
  SimpleGrid, Table, Divider,
} from '../../components/gab/GabComponents';
import {
  IconPrinter, IconDownload, IconFileZip,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import JSZip from 'jszip';
import { useDebrisStore } from '../../store/debrisStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useTampaDebrisStore } from '../../store/tampaDebrisStore';
import { useAuthStore } from '../../store/authStore';
import { TABLES } from '../../lib/api-switch';
import { FIELDS } from '../../api/fields';
import { DEBRIS_TYPES, DEBRIS_TYPE_LABELS, TICKET_STATUS_LABELS, TICKET_STATUS_COLORS } from '../../types/debris';
import type { DebrisType, TicketStatus } from '../../types/debris';

type ReportType = 'daily_ops' | 'zone_summary' | 'contractor_reconciliation' | 'fema_pa' | 'disposal_site' | 'verification_log' | 'pickup_coordinates' | 'variance_summary';

const REPORT_TYPES: { value: ReportType; label: string; description: string }[] = [
  // daily_ops, zone_summary, disposal_site hidden — not applicable to Tampa's pay-first reconciliation workflow
  { value: 'contractor_reconciliation', label: 'Contractor Reconciliation', description: 'Tickets and amounts per contractor' },
  { value: 'fema_pa', label: 'FEMA Public Assistance', description: 'Formatted for federal reimbursement' },
  { value: 'verification_log', label: 'Verification Log', description: 'Monitor sign-offs, timestamps, disputes' },
  { value: 'pickup_coordinates', label: 'Pickup Site Coordinates', description: 'All pickup stops with lat/lng, debris type, and quantity' },
  { value: 'variance_summary', label: 'Variance Summary', description: 'Invoice vs. verified vs. approved amounts by contractor and period' },
];

const fmtMoney = (v: number) =>
  v > 0 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';

// Format measurement for a ticket based on its mode
function formatMeasurement(t: { measurementMode?: string; calculatedCubicYards?: string; loadPercentage?: string; unitCount?: string; weight?: string; weightUnit?: string; debrisType?: string }) {
  const cfg = t.debrisType ? DEBRIS_TYPES[t.debrisType as DebrisType] : null;
  const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';
  if (mode === 'unit') return `${t.unitCount || 0} ${cfg?.unitLabel || 'units'}`;
  if (mode === 'weight') return `${parseFloat(t.weight || '0').toLocaleString()} ${t.weightUnit || 'lbs'}`;
  return `${parseFloat(t.calculatedCubicYards || '0').toLocaleString()} CY`;
}

export default function DebrisReports() {
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';
  const printRef = useRef<HTMLDivElement>(null);

  const { zones, contractors, disposalSites, loadZones, loadContractors, loadDisposalSites } = useDebrisStore();
  const { loadInvoices, invoices } = useDebrisOpsStore();
  const { reconciliations, loadReconciliations } = useTampaDebrisStore();

  // Direct API loading of ALL tickets (bypasses 100-record store pagination)
  const [allTickets, setAllTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);

  useEffect(() => {
    if (!incidentId) return;
    let cancelled = false;
    setLoadingTickets(true);
    (async () => {
      try {
        const { queryRecords } = await import('../../api/client');
        const { TABLES: T } = await import('../../api/tables');
        const { FIELDS: F } = await import('../../api/fields');
        const FT = F.debrisLoadTickets;
        const PAGE = 500;
        let offset = 0, total = Infinity;
        const all: any[] = [];
        while (offset < total && !cancelled) {
          const res = await queryRecords<Record<string, unknown>>(T.debrisLoadTickets, {
            limit: PAGE, offset,
            filters: [{ field: FT.incidentId, operator: 'eq' as const, value: incidentId }],
          });
          total = res.total;
          res.records.forEach((r: any) => {
            all.push({
              id: r.record_id,
              ticketNumber: String(r[FT.ticketNumber] || ''),
              contractorId: String(r[FT.contractorId] || ''),
              debrisType: String(r[FT.debrisType] || ''),
              calculatedCubicYards: String(r[FT.calculatedCubicYards] || '0'),
              status: String(r[FT.status] || ''),
              disposalSiteId: String(r[FT.disposalSiteId] || ''),
              clinCode: String(r[FT.clinCode] || ''),
              extendedCost: String(r[FT.extendedCost] || ''),
              transactionRate: String(r[FT.transactionRate] || ''),
              subcontractorName: String(r[FT.subcontractorName] || ''),
              truckNumber: String(r[FT.truckNumber] || ''),
              loadDate: String(r[FT.loadDate] || ''),
              disposalSiteName: String(r[FT.disposalSiteName] || ''),
              pickupLat: String(r[FT.pickupLat] || ''),
              pickupLng: String(r[FT.pickupLng] || ''),
              unitCount: String(r[FT.unitCount] || '0'),
              weight: String(r[FT.weight] || '0'),
              zoneId: String(r[FT.zoneId] || ''),
              pickupAddress: String(r[FT.pickupAddress] || ''),
              verifiedAt: String(r[FT.verifiedAt] || ''),
              monitorName: String(r[FT.loadingMonitorName] || ''),
              loadPercentage: String(r[FT.loadPercentage] || ''),
              weightUnit: String(r[FT.weightUnit] || ''),
              measurementMode: String(r[FT.measurementMode] || ''),
              pickupTime: String(r[FT.pickupTime] || ''),
              pickupStopsJson: String(r[FT.pickupStopsJson] || '[]'),
              notes: String(r[FT.notes] || ''),
            });
          });
          offset += PAGE;
        }
        if (!cancelled) setAllTickets(all);
      } catch (e) { console.error('Failed to load all tickets:', e); }
      if (!cancelled) setLoadingTickets(false);
    })();
    return () => { cancelled = true; };
  }, [incidentId]);

  const [reportType, setReportType] = useState<ReportType>('contractor_reconciliation');
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadZones(); loadContractors(); loadDisposalSites();
    loadInvoices(); loadReconciliations();
  }, [loadZones, loadContractors, loadDisposalSites, loadInvoices, loadReconciliations]);

  const iZones = incidentId ? zones : [];
  const iContractors = incidentId ? contractors : [];
  const iSites = incidentId ? disposalSites : [];
  const iTickets = incidentId ? allTickets : [];

  const totalCY = useMemo(() => iTickets.reduce((s, t) => s + (parseFloat(t.calculatedCubicYards) || 0), 0), [iTickets]);
  const totalEstCY = useMemo(() => iZones.reduce((s, z) => s + (parseFloat(z.estimatedCubicYards) || 0), 0), [iZones]);
  const totalUnits = useMemo(() => iTickets.reduce((s, t) => s + (parseFloat(t.unitCount) || 0), 0), [iTickets]);
  const totalWeight = useMemo(() => iTickets.reduce((s, t) => s + (parseFloat(t.weight) || 0), 0), [iTickets]);
  const contractorName = (id: string) => contractors.find(c => String(c.id) === id)?.companyName ?? id;
  const zoneName = (id: string) => zones.find(z => String(z.id) === id)?.zoneName ?? `Zone ${id}`;
  const siteName = (id: string) => disposalSites.find(s => String(s.id) === id)?.siteName ?? id;

  // Count stops across all tickets
  const totalStops = useMemo(() => {
    return iTickets.reduce((s, t) => {
      try { return s + JSON.parse(t.pickupStopsJson || '[]').length; } catch { return s; }
    }, 0);
  }, [iTickets]);

  // Flattened pickup stops for coordinates report
  const allPickupStops = useMemo(() => {
    const rows: { ticketNumber: string; zone: string; address: string; lat: number | null; lng: number | null; debrisType: string; measurement: string; timestamp: string; notes: string }[] = [];
    iTickets.forEach(t => {
      let stops: any[] = [];
      try { stops = JSON.parse(t.pickupStopsJson || '[]'); } catch { return; }
      stops.forEach((s: any) => {
        rows.push({
          ticketNumber: t.ticketNumber,
          zone: zoneName(t.zoneId),
          address: s.address || '',
          lat: (s.lat != null && s.lat !== 0) ? Number(s.lat) : null,
          lng: (s.lng != null && s.lng !== 0) ? Number(s.lng) : null,
          debrisType: DEBRIS_TYPE_LABELS[s.debrisType as DebrisType] ?? s.debrisType ?? '',
          measurement: formatMeasurement({ ...t, ...s, measurementMode: s.measurementMode }),
          timestamp: s.timestamp || t.pickupTime || '',
          notes: s.notes || '',
        });
      });
    });
    return rows;
  }, [iTickets, zones]);

  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html><head><title>Debris Report - ${REPORT_TYPES.find(r => r.value === reportType)?.label}</title>
          <style>body{font-family:Arial,sans-serif;padding:24px;font-size:12px}
          table{width:100%;border-collapse:collapse;margin:12px 0}
          th,td{border:1px solid #ccc;padding:6px 8px;text-align:left}
          th{background:#f5f5f5;font-weight:700}
          h1{font-size:18px}h2{font-size:14px;margin-top:16px}
          .header{border-bottom:2px solid #333;padding-bottom:8px;margin-bottom:16px}
          .summary{background:#f9f9f9;padding:12px;border-radius:4px;margin:12px 0}
          @media print{body{padding:0}}</style>
          </head><body>${printRef.current.innerHTML}</body></html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      }
    }
  };

  const exportCSV = () => {
    let csv = '';
    if (reportType === 'daily_ops' || reportType === 'fema_pa') {
      csv = 'Ticket#,Truck#,DebrisType,MeasurementMode,CubicYards,LoadPct,UnitCount,Weight,WeightUnit,Zone,PickupAddress,Stops,PickupTime,VerifiedAt,Status,MonitorName\n';
      iTickets.forEach(t => {
        const stops = (() => { try { return JSON.parse(t.pickupStopsJson || '[]').length; } catch { return 0; } })();
        csv += `"${t.ticketNumber}","${t.truckNumber}","${t.debrisType}","${t.measurementMode || 'volumetric'}",${t.calculatedCubicYards || 0},${t.loadPercentage || 0},${t.unitCount || 0},${t.weight || 0},"${t.weightUnit || ''}","${zoneName(t.zoneId)}","${t.pickupAddress}",${stops},"${t.pickupTime}","${t.verifiedAt || ''}","${t.status}","${t.monitorName}"\n`;
      });
    } else if (reportType === 'verification_log') {
      csv = 'Ticket#,Status,MonitorName,VerifiedAt,DebrisType,Measurement,DisposalSite,Notes\n';
      iTickets.filter(t => t.verifiedAt || t.status === 'verified' || t.status === 'delivered' || t.status === 'disputed').forEach(t => {
        csv += `"${t.ticketNumber}","${t.status}","${t.monitorName}","${t.verifiedAt || ''}","${t.debrisType}","${formatMeasurement(t)}","${siteName(t.disposalSiteId)}","${(t.notes || '').replace(/"/g, '""')}"\n`;
      });
    } else if (reportType === 'zone_summary') {
      csv = 'Zone,Code,Status,EstimatedCY,ActualCY,CompletionPct,Contractor\n';
      iZones.forEach(z => {
        const est = parseFloat(z.estimatedCubicYards || '0');
        const act = parseFloat(z.actualCubicYards || '0');
        csv += `"${z.zoneName}","${z.zoneCode}","${z.status}",${est},${act},${est > 0 ? ((act / est) * 100).toFixed(1) : 0},"${z.assignedContractorId ? contractorName(z.assignedContractorId) : ''}"\n`;
      });
    } else if (reportType === 'pickup_coordinates') {
      csv = 'Ticket#,Zone,Address,Latitude,Longitude,DebrisType,Measurement,PickupDateTime,Notes\n';
      allPickupStops.forEach(s => {
        const dt = s.timestamp ? new Date(s.timestamp).toLocaleString() : '';
        csv += `"${s.ticketNumber}","${s.zone}","${s.address.replace(/"/g, '""')}",${s.lat ?? ''},${s.lng ?? ''},"${s.debrisType}","${s.measurement}","${dt}","${s.notes.replace(/"/g, '""')}"\n`;
      });
    } else if (reportType === 'contractor_reconciliation') {
      csv = 'Contractor,TicketCount,DebrisType,MeasurementMode,Quantity,Unit,Rate,Amount\n';
      iContractors.forEach(c => {
        const cTickets = iTickets.filter(t => t.contractorId === String(c.id));
        const byType: Record<string, { count: number; cy: number; units: number; weight: number; mode: string }> = {};
        cTickets.forEach(t => {
          const dt = t.debrisType || 'unknown';
          const cfg = DEBRIS_TYPES[dt as DebrisType];
          const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';
          if (!byType[dt]) byType[dt] = { count: 0, cy: 0, units: 0, weight: 0, mode };
          byType[dt].count++;
          if (mode === 'volumetric') byType[dt].cy += parseFloat(t.calculatedCubicYards || '0');
          else if (mode === 'unit') byType[dt].units += parseFloat(t.unitCount || '0');
          else if (mode === 'weight') byType[dt].weight += parseFloat(t.weight || '0');
        });
        Object.entries(byType).forEach(([type, data]) => {
          const qty = data.mode === 'volumetric' ? data.cy : data.mode === 'unit' ? data.units : data.weight;
          const unit = data.mode === 'volumetric' ? 'CY' : data.mode === 'unit' ? 'units' : 'lbs';
          csv += `"${c.companyName}",${data.count},"${DEBRIS_TYPE_LABELS[type as DebrisType] ?? type}","${data.mode}",${qty.toFixed(1)},"${unit}","",""\n`;
        });
      });
    } else if (reportType === 'variance_summary') {
      csv = 'Contractor,Period Start,Period End,Invoice #,Invoiced,Verified,Approved,Variance $,Variance %,Status\n';
      reconciliations.forEach(r => {
        const inv = invoices.find(i => String(i.id) === String(r.invoiceId));
        const totInv = Number(r.totalInvoiced) || 0;
        const totVer = Number(r.totalVerified) || 0;
        const totApp = Number(r.approvedAmount) || 0;
        const varAmt = totInv - totVer;
        const varPct = totInv > 0 ? (varAmt / totInv) * 100 : 0;
        csv += `"${contractorName(String(r.contractorId || ''))}","${r.periodStart?.slice(0,10) || ''}","${r.periodEnd?.slice(0,10) || ''}","${inv?.invoiceNumber || r.invoiceId || ''}",${totInv.toFixed(2)},${totVer.toFixed(2)},${totApp.toFixed(2)},${varAmt.toFixed(2)},${varPct.toFixed(1)},"${r.status}"\n`;
      });
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debris-${reportType}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    notifications.show({ message: 'CSV exported', color: 'var(--color-success)' });
  };

  const reportDateDisplay = new Date(reportDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const dailyTickets = useMemo(() => {
    return iTickets.filter(t => {
      if (!t.pickupTime) return false;
      const tDate = t.pickupTime.includes('T') ? t.pickupTime.split('T')[0] : t.pickupTime.split(' ')[0];
      return tDate === reportDate;
    });
  }, [iTickets, reportDate]);

  // Status breakdown
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    iTickets.forEach(t => { counts[t.status] = (counts[t.status] || 0) + 1; });
    return counts;
  }, [iTickets]);

  const [exporting, setExporting] = useState(false);

  const bulkExport = async () => {
    setExporting(true);
    try {
      const zip = new JSZip();
      const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const toCsv = (headers: string[], rows: Record<string, unknown>[]) => {
        let csv = headers.map(h => esc(h)).join(',') + '\n';
        rows.forEach(r => { csv += headers.map(h => esc(r[h])).join(',') + '\n'; });
        return csv;
      };

      const FIN = FIELDS.debrisInvoices;
      const mapInvoice = (r: Record<string, unknown>) => ({
        id: r.record_id, invoiceNumber: r[FIN.invoiceNumber], contractorId: r[FIN.contractorId],
        invoiceDate: r[FIN.invoiceDate], periodStart: r[FIN.periodStart], periodEnd: r[FIN.periodEnd],
        totalAmount: r[FIN.totalAmount], status: r[FIN.status], incidentId: r[FIN.incidentId],
      });

      // Load all tables in parallel
      const { queryRecords } = await import('../../api/client');
      const [ticketRows, contractorRows, zoneRows, siteRows, rawInvoiceRows] = await Promise.all([
        Promise.resolve(iTickets),
        Promise.resolve(iContractors),
        Promise.resolve(iZones),
        Promise.resolve(iSites),
        TABLES.debrisInvoices ? queryRecords(TABLES.debrisInvoices, { limit: 500 }).then(r => r.records).catch(() => []) : Promise.resolve([]),
      ]);
      const invoiceRows = (rawInvoiceRows as Record<string, unknown>[]).map(mapInvoice);

      // Load Tickets — comprehensive
      zip.file('load_tickets.csv', toCsv(
        ['ticketNumber', 'status', 'debrisType', 'measurementMode', 'calculatedCubicYards', 'loadPercentage',
         'unitCount', 'unitSubcategory', 'weight', 'weightUnit', 'scaleTicketNumber',
         'truckNumber', 'contractorId', 'crewId', 'zoneId', 'disposalSiteId',
         'pickupAddress', 'pickupLat', 'pickupLng', 'pickupTime', 'disposalTime',
         'loadingMonitorName', 'loadingLoadPct', 'towerLoadPct', 'monitorName', 'verifiedAt',
         'handLoaded', 'notes', 'incidentId'],
        ticketRows as any[],
      ));

      // Pickup stops (exploded — one row per stop)
      const stopRows: Record<string, unknown>[] = [];
      (ticketRows as any[]).forEach(t => {
        try {
          const stops = JSON.parse(t.pickupStopsJson || '[]');
          stops.forEach((s: any, idx: number) => {
            stopRows.push({ ticketNumber: t.ticketNumber, stopIndex: idx + 1, address: s.address, lat: s.lat, lng: s.lng, debrisType: s.debrisType, unitCount: s.unitCount, weight: s.weight, notes: s.notes, timestamp: s.timestamp });
          });
        } catch { /* skip */ }
      });
      if (stopRows.length > 0) {
        zip.file('pickup_stops.csv', toCsv(
          ['ticketNumber', 'stopIndex', 'address', 'lat', 'lng', 'debrisType', 'unitCount', 'weight', 'notes', 'timestamp'],
          stopRows,
        ));
      }

      // Contractors
      zip.file('contractors.csv', toCsv(
        ['id', 'companyName', 'contactName', 'contactEmail', 'contactPhone', 'status', 'rateVegetative', 'rateCAndD', 'rateWhiteGoods', 'incidentId'],
        contractorRows as any[],
      ));

      // Zones
      zip.file('zones.csv', toCsv(
        ['id', 'zoneName', 'zoneCode', 'status', 'estimatedCubicYards', 'actualCubicYards', 'assignedContractorId', 'incidentId'],
        zoneRows as any[],
      ));

      // Disposal Sites
      zip.file('disposal_sites.csv', toCsv(
        ['id', 'siteName', 'siteType', 'status', 'address', 'latitude', 'longitude', 'capacityCubicYards', 'currentVolume', 'monitorName', 'operatingHours', 'acceptedTypes', 'incidentId'],
        siteRows as any[],
      ));

      // Invoices
      if ((invoiceRows as any[]).length > 0) {
        zip.file('invoices.csv', toCsv(
          ['id', 'invoiceNumber', 'contractorId', 'invoiceDate', 'periodStart', 'periodEnd', 'totalAmount', 'status', 'incidentId'],
          invoiceRows as any[],
        ));
      }

      // Generate and download
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debris-bulk-export-${new Date().toISOString().split('T')[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      notifications.show({ message: `Exported ${Object.keys(zip.files).length} files`, color: 'var(--color-success)' });
    } catch (err) {
      notifications.show({ message: err instanceof Error ? err.message : 'Export failed', color: 'var(--color-error)' });
    } finally { setExporting(false); }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Reports</Title>
        <Group gap="xs">
          <Button size="xs" variant="filled" leftSection={<IconFileZip size={14} />} onClick={bulkExport} loading={exporting}>Bulk Export (All Data)</Button>
          <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={exportCSV}>Export CSV</Button>
          <Button size="xs" variant="light" leftSection={<IconPrinter size={14} />} onClick={handlePrint}>Print</Button>
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 3, md: 6 }}>
        {REPORT_TYPES.map(rt => (
          <Paper key={rt.value} p="sm" radius="md" withBorder
            style={{ cursor: 'pointer', border: reportType === rt.value ? '2px solid var(--color-primary-700)' : undefined }}
            onClick={() => setReportType(rt.value)}>
            <Text size="sm" fw={600}>{rt.label}</Text>
            <Text size="xs" c="dimmed">{rt.description}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {(reportType === 'daily_ops' || reportType === 'verification_log') && (
        <Group gap="sm">
          <TextInput type="date" label="Report Date" value={reportDate}
            onChange={e => setReportDate(e.target.value)} size="xs" w={180} />
          <Text size="xs" c="dimmed" mt={24}>
            {reportType === 'daily_ops' ? `${dailyTickets.length} tickets on this date` : ''}
          </Text>
        </Group>
      )}

      <Paper p="lg" radius="md" withBorder ref={printRef}>
        <div className="header">
          <Text size="lg" fw={700}>OpenGov EMC — Debris ADMS</Text>
          <Text size="sm" fw={600}>{REPORT_TYPES.find(r => r.value === reportType)?.label}</Text>
          <Text size="xs" c="dimmed">
            Incident: {activeIncident?.name ?? 'All'} &middot; Generated: {new Date().toLocaleString()}
          </Text>
        </div>

        {/* Summary box */}
        <div className="summary" style={{ background: '#F8F8F8', padding: 16, borderRadius: 4, marginBottom: 16 }}>
          <SimpleGrid cols={{ base: 3, sm: 6 }}>
            <div><Text size="xs" fw={700}>Load Tickets</Text><Text fw={600}>{iTickets.length}</Text></div>
            <div><Text size="xs" fw={700}>Pickup Stops</Text><Text fw={600}>{totalStops}</Text></div>
            <div><Text size="xs" fw={700}>Total CY</Text><Text fw={600}>{totalCY.toLocaleString()}</Text></div>
            {totalUnits > 0 && <div><Text size="xs" fw={700}>Total Units</Text><Text fw={600}>{totalUnits.toLocaleString()}</Text></div>}
            {totalWeight > 0 && <div><Text size="xs" fw={700}>Total Weight</Text><Text fw={600}>{totalWeight.toLocaleString()} lbs</Text></div>}
            <div><Text size="xs" fw={700}>Completion</Text><Text fw={600}>{totalEstCY > 0 ? ((totalCY / totalEstCY) * 100).toFixed(1) : 0}%</Text></div>
          </SimpleGrid>
          {/* Status breakdown */}
          <Group gap="xs" mt="xs">
            {Object.entries(statusCounts).sort(([, a], [, b]) => b - a).map(([status, count]) => (
              <Badge key={status} size="sm" variant="light" color={TICKET_STATUS_COLORS[status as TicketStatus] ?? '#939598'}>
                {TICKET_STATUS_LABELS[status as TicketStatus] ?? status}: {count}
              </Badge>
            ))}
          </Group>
        </div>

        {/* ── Daily Operations ──────────────────────── */}
        {reportType === 'daily_ops' && (
          <>
            <Text fw={700} mb="xs">Load Tickets — {reportDateDisplay}</Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Ticket #</Table.Th><Table.Th>Truck</Table.Th><Table.Th>Type</Table.Th>
                  <Table.Th ta="right">Measurement</Table.Th><Table.Th ta="center">Stops</Table.Th>
                  <Table.Th>Zone</Table.Th><Table.Th>Pickup</Table.Th>
                  <Table.Th>Verified</Table.Th><Table.Th>Status</Table.Th><Table.Th>Monitor</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {dailyTickets.sort((a, b) => (b.id || 0) - (a.id || 0)).map(t => {
                  const stops = (() => { try { return JSON.parse(t.pickupStopsJson || '[]').length; } catch { return 0; } })();
                  return (
                    <Table.Tr key={t.id}>
                      <Table.Td>{t.ticketNumber}</Table.Td>
                      <Table.Td>{t.truckNumber || '-'}</Table.Td>
                      <Table.Td>{DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType}</Table.Td>
                      <Table.Td ta="right" fw={600}>{formatMeasurement(t)}</Table.Td>
                      <Table.Td ta="center">{stops || '-'}</Table.Td>
                      <Table.Td>{t.zoneId ? zoneName(t.zoneId) : '-'}</Table.Td>
                      <Table.Td>{t.pickupTime ? new Date(t.pickupTime).toLocaleTimeString() : '-'}</Table.Td>
                      <Table.Td>{t.verifiedAt ? new Date(t.verifiedAt).toLocaleTimeString() : '-'}</Table.Td>
                      <Table.Td><Badge size="xs" color={TICKET_STATUS_COLORS[t.status as TicketStatus] ?? '#939598'}>{t.status?.replace(/_/g, ' ')}</Badge></Table.Td>
                      <Table.Td>{t.monitorName || '-'}</Table.Td>
                    </Table.Tr>
                  );
                })}
                {dailyTickets.length === 0 && (
                  <Table.Tr><Table.Td colSpan={10} ta="center" c="dimmed">No tickets for this date</Table.Td></Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </>
        )}

        {/* ── Zone Summary ──────────────────────── */}
        {reportType === 'zone_summary' && (
          <>
            <Text fw={700} mb="xs">Zone Progress</Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Zone</Table.Th><Table.Th>Code</Table.Th><Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Est CY</Table.Th><Table.Th ta="right">Actual CY</Table.Th>
                  <Table.Th ta="right">Completion</Table.Th><Table.Th>Contractor</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iZones.map(z => {
                  const est = parseFloat(z.estimatedCubicYards || '0');
                  const act = parseFloat(z.actualCubicYards || '0');
                  const pct = est > 0 ? ((act / est) * 100).toFixed(1) : '0';
                  return (
                    <Table.Tr key={z.id}>
                      <Table.Td fw={500}>{z.zoneName}</Table.Td>
                      <Table.Td>{z.zoneCode}</Table.Td>
                      <Table.Td><Badge size="xs">{z.status}</Badge></Table.Td>
                      <Table.Td ta="right">{est.toLocaleString()}</Table.Td>
                      <Table.Td ta="right">{act.toLocaleString()}</Table.Td>
                      <Table.Td ta="right">{pct}%</Table.Td>
                      <Table.Td>{z.assignedContractorId ? contractorName(z.assignedContractorId) : '-'}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </>
        )}

        {/* ── Contractor Reconciliation ──────────── */}
        {reportType === 'contractor_reconciliation' && (
          <>
            <Text fw={700} mb="xs">Contractor Reconciliation</Text>
            {iContractors.map(c => {
              const cTickets = iTickets.filter(t => t.contractorId === String(c.id));
              if (cTickets.length === 0) return null;
              // Group by debris type with measurement mode awareness
              const byType: Record<string, { count: number; cy: number; units: number; weight: number; mode: string }> = {};
              cTickets.forEach(t => {
                const dt = t.debrisType || 'unknown';
                const cfg = DEBRIS_TYPES[dt as DebrisType];
                const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';
                if (!byType[dt]) byType[dt] = { count: 0, cy: 0, units: 0, weight: 0, mode };
                byType[dt].count++;
                if (mode === 'volumetric') byType[dt].cy += parseFloat(t.calculatedCubicYards || '0');
                else if (mode === 'unit') byType[dt].units += parseFloat(t.unitCount || '0');
                else if (mode === 'weight') byType[dt].weight += parseFloat(t.weight || '0');
              });
              return (
                <Paper key={c.id} p="sm" mb="md" withBorder>
                  <Group justify="space-between" mb="xs">
                    <Text fw={600}>{c.companyName}</Text>
                    <Text fw={600}>{cTickets.length} ticket{cTickets.length !== 1 ? 's' : ''}</Text>
                  </Group>
                  <Table striped withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Debris Type</Table.Th><Table.Th ta="right">Tickets</Table.Th>
                        <Table.Th ta="right">Quantity</Table.Th><Table.Th>Unit</Table.Th>
                        <Table.Th ta="right">Rate</Table.Th><Table.Th ta="right">Amount</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {Object.entries(byType).map(([type, data]) => {
                        const RATE_MAP: Record<string, keyof typeof c> = {
                          vegetative: 'rateVegetative', c_and_d: 'rateCAndD',
                          soil_mud_sand: 'rateSoilMudSand', hazardous_tree: 'rateHazardousTree',
                          stump: 'rateStumpRemoval', white_goods: 'rateWhiteGoods',
                          e_waste: 'rateElectronic', hhw: 'rateHhw',
                        };
                        const rateKey = RATE_MAP[type];
                        const rate = rateKey ? parseFloat((c[rateKey] as string) || '0') : 0;
                        const qty = data.mode === 'volumetric' ? data.cy : data.mode === 'unit' ? data.units : data.weight;
                        const unit = data.mode === 'volumetric' ? 'CY' : data.mode === 'unit' ? (DEBRIS_TYPES[type as DebrisType]?.unitLabel || 'units') : 'lbs';
                        const amount = rate > 0 ? qty * rate : 0;
                        return (
                          <Table.Tr key={type}>
                            <Table.Td>{DEBRIS_TYPE_LABELS[type as DebrisType] ?? type}</Table.Td>
                            <Table.Td ta="right">{data.count}</Table.Td>
                            <Table.Td ta="right">{qty.toLocaleString()}</Table.Td>
                            <Table.Td>{unit}</Table.Td>
                            <Table.Td ta="right">{rate > 0 ? `$${rate.toFixed(2)}/${unit}` : '-'}</Table.Td>
                            <Table.Td ta="right" fw={600}>{amount > 0 ? `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}</Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Paper>
              );
            })}
          </>
        )}

        {/* ── FEMA PA ──────────────────────── */}
        {reportType === 'fema_pa' && (
          <>
            <Text fw={700} mb="xs">FEMA Public Assistance — Debris Removal Documentation</Text>
            <Divider mb="sm" />
            <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
              <div><Text size="xs" fw={700}>Applicant</Text><Text size="sm">City of Tampa, FL</Text></div>
              <div><Text size="xs" fw={700}>Disaster Number</Text><Text size="sm">Pending</Text></div>
              <div><Text size="xs" fw={700}>Category</Text><Text size="sm">Category A — Debris Removal</Text></div>
              <div><Text size="xs" fw={700}>Report Period</Text><Text size="sm">{reportDateDisplay}</Text></div>
              <div><Text size="xs" fw={700}>Total CY Removed</Text><Text size="sm" fw={600}>{totalCY.toLocaleString()}</Text></div>
              {totalUnits > 0 && <div><Text size="xs" fw={700}>Total Units Removed</Text><Text size="sm" fw={600}>{totalUnits.toLocaleString()}</Text></div>}
              {totalWeight > 0 && <div><Text size="xs" fw={700}>Total Weight Removed</Text><Text size="sm" fw={600}>{totalWeight.toLocaleString()} lbs</Text></div>}
              <div><Text size="xs" fw={700}>Pickup Locations</Text><Text size="sm">{totalStops} stops across {iTickets.length} tickets</Text></div>
              <div><Text size="xs" fw={700}>Active Zones</Text><Text size="sm">{iZones.filter(z => z.status === 'active').length} of {iZones.length}</Text></div>
              <div><Text size="xs" fw={700}>Contractors</Text><Text size="sm">{iContractors.filter(c => c.status === 'active').length} active</Text></div>
              <div><Text size="xs" fw={700}>Disposal Sites</Text><Text size="sm">{iSites.filter(s => s.status === 'open').length} open</Text></div>
              <div><Text size="xs" fw={700}>Verified Tickets</Text><Text size="sm">{iTickets.filter(t => t.status === 'verified' || t.status === 'delivered').length} of {iTickets.length}</Text></div>
            </SimpleGrid>

            <Text fw={600} size="sm" mb="xs">Debris by Type</Text>
            <Table striped withTableBorder mb="md">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th><Table.Th>Mode</Table.Th><Table.Th ta="right">Tickets</Table.Th>
                  <Table.Th ta="right">Quantity</Table.Th><Table.Th>Unit</Table.Th><Table.Th ta="right">% of Tickets</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(() => {
                  const byType: Record<string, { count: number; cy: number; units: number; weight: number; mode: string }> = {};
                  iTickets.forEach(t => {
                    const dt = t.debrisType || 'unknown';
                    const cfg = DEBRIS_TYPES[dt as DebrisType];
                    const mode = t.measurementMode || cfg?.measurementMode || 'volumetric';
                    if (!byType[dt]) byType[dt] = { count: 0, cy: 0, units: 0, weight: 0, mode };
                    byType[dt].count++;
                    if (mode === 'volumetric') byType[dt].cy += parseFloat(t.calculatedCubicYards || '0');
                    else if (mode === 'unit') byType[dt].units += parseFloat(t.unitCount || '0');
                    else if (mode === 'weight') byType[dt].weight += parseFloat(t.weight || '0');
                  });
                  return Object.entries(byType).sort(([, a], [, b]) => b.count - a.count).map(([type, data]) => {
                    const qty = data.mode === 'volumetric' ? data.cy : data.mode === 'unit' ? data.units : data.weight;
                    const unit = data.mode === 'volumetric' ? 'CY' : data.mode === 'unit' ? (DEBRIS_TYPES[type as DebrisType]?.unitLabel || 'units') : 'lbs';
                    return (
                      <Table.Tr key={type}>
                        <Table.Td>{DEBRIS_TYPE_LABELS[type as DebrisType] ?? type}</Table.Td>
                        <Table.Td><Badge size="xs" variant="light">{data.mode}</Badge></Table.Td>
                        <Table.Td ta="right">{data.count}</Table.Td>
                        <Table.Td ta="right">{qty.toLocaleString()}</Table.Td>
                        <Table.Td>{unit}</Table.Td>
                        <Table.Td ta="right">{iTickets.length > 0 ? ((data.count / iTickets.length) * 100).toFixed(1) : 0}%</Table.Td>
                      </Table.Tr>
                    );
                  });
                })()}
              </Table.Tbody>
            </Table>

            <Text size="xs" c="dimmed" mt="lg">
              This report is generated by the OpenGov EMC Debris ADMS and is intended
              to support FEMA Public Assistance Category A reimbursement claims.
              Load ticket documentation is sourced from TDMS contractor data and reconciled against paid invoices.
            </Text>
          </>
        )}

        {/* ── Disposal Site Status ──────────── */}
        {reportType === 'disposal_site' && (
          <>
            <Text fw={700} mb="xs">Disposal Site Status</Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Site</Table.Th><Table.Th>Type</Table.Th><Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Capacity CY</Table.Th><Table.Th ta="right">Current CY</Table.Th>
                  <Table.Th ta="right">Fill %</Table.Th><Table.Th ta="right">Tickets</Table.Th>
                  <Table.Th>Monitor</Table.Th><Table.Th>Hours</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iSites.map(s => {
                  const cap = parseFloat(s.capacityCubicYards || '0');
                  const vol = parseFloat(s.currentVolume || '0');
                  const siteTickets = iTickets.filter(t => t.disposalSiteId === String(s.id));
                  return (
                    <Table.Tr key={s.id}>
                      <Table.Td fw={500}>{s.siteName}</Table.Td>
                      <Table.Td>{s.siteType}</Table.Td>
                      <Table.Td><Badge size="xs" color={s.status === 'open' ? 'var(--color-success)' : s.status === 'full' ? '#F86A0B' : '#939598'}>{s.status}</Badge></Table.Td>
                      <Table.Td ta="right">{cap.toLocaleString()}</Table.Td>
                      <Table.Td ta="right">{vol.toLocaleString()}</Table.Td>
                      <Table.Td ta="right">{cap > 0 ? ((vol / cap) * 100).toFixed(1) : 0}%</Table.Td>
                      <Table.Td ta="right">{siteTickets.length}</Table.Td>
                      <Table.Td>{s.monitorName || '-'}</Table.Td>
                      <Table.Td>{s.operatingHours || '-'}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </>
        )}

        {/* ── Verification Log ──────────── */}
        {reportType === 'verification_log' && (
          <>
            <Text fw={700} mb="xs">Monitor Verification Log</Text>
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Ticket #</Table.Th><Table.Th>Monitor</Table.Th>
                  <Table.Th>Verified At</Table.Th><Table.Th>Type</Table.Th>
                  <Table.Th ta="right">Measurement</Table.Th><Table.Th>Disposal Site</Table.Th>
                  <Table.Th>Status</Table.Th><Table.Th>Notes</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {iTickets
                  .filter(t => t.verifiedAt || t.monitorName || ['verified', 'delivered', 'disputed'].includes(t.status))
                  .sort((a, b) => (b.verifiedAt || '').localeCompare(a.verifiedAt || ''))
                  .map(t => (
                    <Table.Tr key={t.id}>
                      <Table.Td fw={500}>{t.ticketNumber}</Table.Td>
                      <Table.Td>{t.monitorName || '-'}</Table.Td>
                      <Table.Td>{t.verifiedAt ? new Date(t.verifiedAt).toLocaleString() : '-'}</Table.Td>
                      <Table.Td>{DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType}</Table.Td>
                      <Table.Td ta="right" fw={600}>{formatMeasurement(t)}</Table.Td>
                      <Table.Td>{t.disposalSiteId ? siteName(t.disposalSiteId) : '-'}</Table.Td>
                      <Table.Td><Badge size="xs" color={TICKET_STATUS_COLORS[t.status as TicketStatus] ?? '#939598'}>{t.status?.replace(/_/g, ' ')}</Badge></Table.Td>
                      <Table.Td><Text size="xs" lineClamp={2}>{t.notes || '-'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                {iTickets.filter(t => t.verifiedAt || t.monitorName).length === 0 && (
                  <Table.Tr><Table.Td colSpan={8} ta="center" c="dimmed">No verified tickets yet</Table.Td></Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </>
        )}

        {/* ── Variance Summary ──────────────────── */}
        {reportType === 'variance_summary' && (
          <div>
            <Text fw={700} size="lg" mb="sm">Variance Summary</Text>
            {reconciliations.length === 0 ? (
              <Text c="dimmed" size="sm">No reconciliation records found.</Text>
            ) : (
              <>
                <SimpleGrid cols={4} spacing="sm" mb="md">
                  {(() => {
                    const totInv = reconciliations.reduce((s, r) => s + (Number(r.totalInvoiced) || 0), 0);
                    const totVer = reconciliations.reduce((s, r) => s + (Number(r.totalVerified) || 0), 0);
                    const totApp = reconciliations.filter(r => r.status === 'approved').reduce((s, r) => s + (Number(r.approvedAmount) || 0), 0);
                    const totalVar = Math.abs(totInv - totVer);
                    return (
                      <>
                        <Paper p="sm" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">Total Invoiced</Text>
                          <Text size="lg" fw={800} c="#3b82f6">{fmtMoney(totInv)}</Text>
                        </Paper>
                        <Paper p="sm" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">Total Verified</Text>
                          <Text size="lg" fw={800} c="var(--color-success)">{fmtMoney(totVer)}</Text>
                        </Paper>
                        <Paper p="sm" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">Total Approved</Text>
                          <Text size="lg" fw={800} c="#16a34a">{fmtMoney(totApp)}</Text>
                        </Paper>
                        <Paper p="sm" withBorder style={{ textAlign: 'center' }}>
                          <Text size="xs" c="dimmed">Total Variance</Text>
                          <Text size="lg" fw={800} c={totalVar > 0 ? '#C68700' : 'var(--color-success)'}>{fmtMoney(totalVar)}</Text>
                        </Paper>
                      </>
                    );
                  })()}
                </SimpleGrid>
                <Paper withBorder>
                  <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 420px)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                          {['Contractor', 'Period', 'Invoice #', 'Invoiced', 'Verified', 'Approved', 'Variance $', 'Var %', 'Status'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Contractor' || h === 'Period' || h === 'Invoice #' || h === 'Status' ? 'left' : 'right', position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1, color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[...reconciliations].sort((a, b) => b.id - a.id).map(r => {
                          const inv = invoices.find(i => String(i.id) === String(r.invoiceId));
                          const totInv = Number(r.totalInvoiced) || 0;
                          const totVer = Number(r.totalVerified) || 0;
                          const totApp = Number(r.approvedAmount) || 0;
                          const varAmt = totInv - totVer;
                          const varPct = totInv > 0 ? (varAmt / totInv) * 100 : 0;
                          const varColor = Math.abs(varPct) > 10 ? '#D33423' : Math.abs(varPct) > 2 ? '#C68700' : '#16a34a';
                          const statusColors: Record<string, string> = {
                            draft: '#939598', in_review: '#3b82f6', approved: '#16a34a', disputed: '#D33423', resolved: '#03AC9E',
                          };
                          const statusLabels: Record<string, string> = {
                            draft: 'Draft', in_review: 'In Review', approved: 'Approved', disputed: 'Disputed', resolved: 'Resolved',
                          };
                          return (
                            <tr key={r.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <td style={{ padding: '6px 12px', fontWeight: 500 }}>{contractorName(String(r.contractorId || ''))}</td>
                              <td style={{ padding: '6px 12px', whiteSpace: 'nowrap', color: '#64748b' }}>{r.periodStart?.slice(0, 10)} — {r.periodEnd?.slice(0, 10)}</td>
                              <td style={{ padding: '6px 12px', fontFamily: 'monospace' }}>{inv?.invoiceNumber || (r.invoiceId ? `#${r.invoiceId}` : '—')}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(totInv)}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(totVer)}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{totApp > 0 ? fmtMoney(totApp) : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace', color: varColor }}>{varAmt >= 0 ? '+' : ''}{fmtMoney(Math.abs(varAmt))}</td>
                              <td style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 700, color: varColor }}>{varPct >= 0 ? '+' : ''}{varPct.toFixed(1)}%</td>
                              <td style={{ padding: '6px 12px' }}>
                                <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: `${statusColors[r.status] || '#939598'}22`, color: statusColors[r.status] || '#939598' }}>
                                  {statusLabels[r.status] || r.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Paper>
              </>
            )}
          </div>
        )}

        {/* ── Pickup Site Coordinates ───────────── */}
        {reportType === 'pickup_coordinates' && (
          <>
            <Group justify="space-between" mb="xs">
              <Text fw={700}>Pickup Site Coordinates — {allPickupStops.length} stops across {iTickets.length} tickets</Text>
              <Text size="xs" c="dimmed">{allPickupStops.filter(s => s.lat != null).length} of {allPickupStops.length} stops have coordinates</Text>
            </Group>
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 520px)' }}>
              <Table striped withTableBorder>
                <Table.Thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                  <Table.Tr>
                    <Table.Th>Ticket #</Table.Th>
                    <Table.Th>Zone</Table.Th>
                    <Table.Th>Address</Table.Th>
                    <Table.Th>Latitude</Table.Th>
                    <Table.Th>Longitude</Table.Th>
                    <Table.Th>Debris Type</Table.Th>
                    <Table.Th ta="right">Measurement</Table.Th>
                    <Table.Th>Pickup Date/Time</Table.Th>
                    <Table.Th>Notes</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {allPickupStops.length === 0 && (
                    <Table.Tr><Table.Td colSpan={8} ta="center" c="dimmed" py="md">No pickup stops recorded yet.</Table.Td></Table.Tr>
                  )}
                  {allPickupStops.map((s, i) => (
                    <Table.Tr key={i}>
                      <Table.Td><Text size="xs" fw={600}>{s.ticketNumber}</Text></Table.Td>
                      <Table.Td><Text size="xs">{s.zone}</Text></Table.Td>
                      <Table.Td><Text size="xs">{s.address || '—'}</Text></Table.Td>
                      <Table.Td>
                        {s.lat != null
                          ? <Text size="xs" style={{ fontFamily: 'monospace' }}>{s.lat.toFixed(6)}</Text>
                          : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td>
                        {s.lng != null
                          ? <Text size="xs" style={{ fontFamily: 'monospace' }}>{s.lng.toFixed(6)}</Text>
                          : <Text size="xs" c="dimmed">—</Text>}
                      </Table.Td>
                      <Table.Td><Badge size="xs" variant="light">{s.debrisType || '—'}</Badge></Table.Td>
                      <Table.Td ta="right"><Text size="xs" fw={600}>{s.measurement}</Text></Table.Td>
                      <Table.Td>
                        <Text size="xs" style={{ whiteSpace: 'nowrap' }}>
                          {s.timestamp ? new Date(s.timestamp).toLocaleString() : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td><Text size="xs" lineClamp={2}>{s.notes || '—'}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          </>
        )}
      </Paper>
    </Stack>
  );
}
